/**
 * Song Detail Page
 * View song details, preview, purchase, and download
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import {
  Music,
  Play,
  Pause,
  Download,
  ShoppingCart,
  Star,
  Clock,
  Heart,
  Share2,
  ChevronLeft,
  User,
  Tag,
  Disc,
  FileAudio,
  Check,
  Loader2,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// License info
const LICENSE_INFO = {
  lease: {
    name: 'Lease License',
    description: 'Non-exclusive license for personal and small commercial use',
    features: ['MP3 & WAV files', 'Up to 5,000 streams', 'Non-exclusive rights', '1 music video'],
  },
  standard: {
    name: 'Standard License',
    description: 'Non-exclusive license with expanded distribution rights',
    features: ['MP3 & WAV files', 'Up to 50,000 streams', 'Non-exclusive rights', '1 music video', 'Radio broadcasting'],
  },
  exclusive: {
    name: 'Exclusive License',
    description: 'Full ownership - song removed from marketplace',
    features: ['MP3, WAV & Stems', 'Unlimited streams', 'Full ownership rights', 'Unlimited music videos', 'Full commercial use'],
  },
};

export default function SongDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const audioRef = useRef(null);
  
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [purchasing, setPurchasing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState('standard');
  const [includeStems, setIncludeStems] = useState(false);
  
  // Fetch song details
  useEffect(() => {
    const fetchSong = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/songs/${id}`);
        if (!response.ok) {
          throw new Error('Song not found');
        }
        const data = await response.json();
        setSong(data);
        
        // Check if user has purchased
        if (user) {
          const purchaseRes = await fetch(
            `${API_URL}/api/songs/purchases/history?buyer_id=${user.id}&page=1&limit=100`
          );
          if (purchaseRes.ok) {
            const purchaseData = await purchaseRes.json();
            const purchased = purchaseData.purchases?.some(p => p.song_id === id);
            setHasPurchased(purchased);
          }
        }
      } catch (error) {
        console.error('Failed to fetch song:', error);
        toast.error('Song not found');
        navigate('/songs');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) fetchSong();
  }, [id, user, navigate]);
  
  // Audio player controls
  useEffect(() => {
    if (!audioRef.current) return;
    
    const audio = audioRef.current;
    
    const handleTimeUpdate = () => {
      setProgress((audio.currentTime / audio.duration) * 100);
    };
    
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
    };
    
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [song]);
  
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };
  
  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = percent * duration;
  };
  
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Calculate total price
  const calculatePrice = () => {
    if (!song) return 0;
    let total = song.price || 0;
    if (includeStems && song.stems_available && song.stems_price) {
      total += song.stems_price;
    }
    return total;
  };
  
  // Handle purchase
  const handlePurchase = async () => {
    if (!user) {
      toast.error('Please log in to purchase');
      navigate('/login');
      return;
    }
    
    if (song.price === 0) {
      // Free song - direct download
      handleDownload();
      return;
    }
    
    setPurchasing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/songs/${id}/purchase?buyer_id=${user.id}&buyer_email=${encodeURIComponent(user.email)}&license_type=${selectedLicense}&include_stems=${includeStems}`,
        { method: 'POST' }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Purchase failed');
      }
      
      toast.success('Purchase successful! You can now download the song.');
      setHasPurchased(true);
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.message || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };
  
  // Handle download
  const handleDownload = async () => {
    if (!user) {
      toast.error('Please log in to download');
      navigate('/login');
      return;
    }
    
    setDownloading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/songs/${id}/download?buyer_id=${user.id}&include_stems=${includeStems}`
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Download failed');
      }
      
      const data = await response.json();
      
      // Convert base64 to blob and download
      const byteCharacters = atob(data.audio_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { 
        type: data.file_format === 'mp3' ? 'audio/mpeg' : 'audio/wav' 
      });
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data.title || 'song'}.${data.file_format || 'mp3'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Download started!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };
  
  // Share song
  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: song?.title,
          text: `Check out "${song?.title}" by ${song?.artist_name}`,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Share error:', error);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
      </div>
    );
  }
  
  if (!song) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-zinc-400">Song not found</p>
          <Button onClick={() => navigate('/songs')} variant="link" className="text-violet-400 mt-2">
            Back to Marketplace
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="bg-zinc-900/50 border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <button
            onClick={() => navigate('/songs')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Marketplace
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Song Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Cover & Title */}
            <div className="flex flex-col sm:flex-row gap-6">
              {/* Cover Art */}
              <div className="w-full sm:w-64 aspect-square rounded-xl overflow-hidden flex-shrink-0 bg-gradient-to-br from-violet-600 to-purple-800 flex items-center justify-center">
                {song.cover_art_url ? (
                  <img src={song.cover_art_url} alt={song.title} className="w-full h-full object-cover" />
                ) : (
                  <Music className="w-20 h-20 text-white/50" />
                )}
              </div>
              
              {/* Title & Meta */}
              <div className="flex-1">
                <h1 className="text-3xl font-bold mb-2">{song.title}</h1>
                <button
                  onClick={() => navigate(`/marketplace/user/${song.artist_id}`)}
                  className="flex items-center gap-2 text-zinc-400 hover:text-violet-400 transition-colors mb-4"
                >
                  <User className="w-4 h-4" />
                  {song.artist_name}
                </button>
                
                <div className="flex flex-wrap gap-4 text-sm text-zinc-400 mb-4">
                  {song.genre && (
                    <span className="flex items-center gap-1">
                      <Tag className="w-4 h-4" />
                      {song.genre}
                      {song.subgenre && ` / ${song.subgenre}`}
                    </span>
                  )}
                  {song.bpm && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {song.bpm} BPM
                    </span>
                  )}
                  {song.key && (
                    <span className="flex items-center gap-1">
                      <Disc className="w-4 h-4" />
                      {song.key}
                    </span>
                  )}
                </div>
                
                {/* Rating */}
                {song.rating_count > 0 && (
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map(star => (
                        <Star
                          key={star}
                          className={`w-5 h-5 ${
                            star <= Math.round(song.average_rating)
                              ? 'text-amber-400 fill-amber-400'
                              : 'text-zinc-600'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-white font-medium">{song.average_rating.toFixed(1)}</span>
                    <span className="text-zinc-500">({song.rating_count} reviews)</span>
                  </div>
                )}
                
                {/* Stats */}
                <div className="flex gap-6 text-sm">
                  <div>
                    <span className="text-zinc-500">Plays</span>
                    <p className="text-white font-medium">{song.play_count || 0}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Sales</span>
                    <p className="text-white font-medium">{song.purchase_count || 0}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Audio Preview Player */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <FileAudio className="w-5 h-5 text-violet-400" />
                Preview
              </h3>
              
              {song.preview_data || song.preview_url ? (
                <>
                  <audio
                    ref={audioRef}
                    src={song.preview_url || `data:audio/mpeg;base64,${song.preview_data}`}
                    preload="metadata"
                  />
                  
                  <div className="flex items-center gap-4">
                    <button
                      onClick={togglePlay}
                      className="w-12 h-12 rounded-full bg-violet-500 hover:bg-violet-400 flex items-center justify-center transition-colors"
                    >
                      {isPlaying ? (
                        <Pause className="w-5 h-5 text-white" />
                      ) : (
                        <Play className="w-5 h-5 text-white ml-0.5" />
                      )}
                    </button>
                    
                    <div className="flex-1">
                      <div
                        onClick={handleSeek}
                        className="h-2 bg-zinc-700 rounded-full cursor-pointer overflow-hidden"
                      >
                        <div
                          className="h-full bg-violet-500 transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-zinc-500 mt-1">
                        <span>{formatTime((progress / 100) * duration)}</span>
                        <span>{formatTime(duration)}</span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-zinc-500 text-center py-4">No preview available</p>
              )}
            </div>
            
            {/* Description */}
            {song.description && (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                <h3 className="font-semibold mb-3">About this track</h3>
                <p className="text-zinc-400 whitespace-pre-wrap">{song.description}</p>
              </div>
            )}
            
            {/* Tags */}
            {song.tags && song.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {song.tags.map((tag, i) => (
                  <span
                    key={`tag-${tag}`}
                    className="px-3 py-1 bg-zinc-800/50 border border-zinc-700 rounded-full text-sm text-zinc-400"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
          
          {/* Right Column - Purchase */}
          <div className="space-y-6">
            {/* Price Card */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 sticky top-6">
              <div className="text-center mb-6">
                <span className="text-4xl font-bold text-white">
                  {song.price === 0 ? 'FREE' : `$${song.price.toFixed(2)}`}
                </span>
                <p className="text-zinc-500 text-sm mt-1">Base price</p>
              </div>
              
              {/* License Selection */}
              {song.price > 0 && (
                <div className="space-y-3 mb-6">
                  <label className="text-sm text-zinc-400">License Type</label>
                  {Object.entries(LICENSE_INFO).map(([key, license]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedLicense(key)}
                      className={`w-full p-4 rounded-lg border text-left transition-colors ${
                        selectedLicense === key
                          ? 'bg-violet-500/20 border-violet-500/50'
                          : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-white">{license.name}</span>
                        {selectedLicense === key && (
                          <Check className="w-4 h-4 text-violet-400" />
                        )}
                      </div>
                      <p className="text-xs text-zinc-500 mt-1">{license.description}</p>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Stems Option */}
              {song.stems_available && song.stems_price && (
                <div className="mb-6">
                  <button
                    onClick={() => setIncludeStems(!includeStems)}
                    className={`w-full flex items-center justify-between p-4 rounded-lg border transition-colors ${
                      includeStems
                        ? 'bg-emerald-500/20 border-emerald-500/50'
                        : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <div>
                      <span className="font-medium text-white">Include Stems</span>
                      <p className="text-xs text-zinc-500">Individual track files</p>
                    </div>
                    <span className="text-emerald-400 font-medium">+${song.stems_price.toFixed(2)}</span>
                  </button>
                </div>
              )}
              
              {/* Total */}
              {(includeStems || selectedLicense !== 'standard') && song.price > 0 && (
                <div className="flex justify-between py-3 border-t border-zinc-800 mb-4">
                  <span className="text-zinc-400">Total</span>
                  <span className="text-xl font-bold text-white">${calculatePrice().toFixed(2)}</span>
                </div>
              )}
              
              {/* Purchase/Download Buttons */}
              {hasPurchased ? (
                <Button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="w-full bg-emerald-600 hover:bg-emerald-500"
                  size="lg"
                >
                  {downloading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5 mr-2" />
                      Download Song
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handlePurchase}
                  disabled={purchasing}
                  className="w-full bg-violet-600 hover:bg-violet-500"
                  size="lg"
                >
                  {purchasing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5 mr-2" />
                      {song.price === 0 ? 'Get Free Download' : 'Purchase Now'}
                    </>
                  )}
                </Button>
              )}
              
              {/* Actions */}
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  onClick={handleShare}
                  className="flex-1 border-zinc-700"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-zinc-700"
                >
                  <Heart className="w-4 h-4 mr-2" />
                  Save
                </Button>
              </div>
              
              {/* License Features */}
              {song.price > 0 && (
                <div className="mt-6 pt-6 border-t border-zinc-800">
                  <h4 className="text-sm font-medium text-zinc-400 mb-3">
                    {LICENSE_INFO[selectedLicense].name} includes:
                  </h4>
                  <ul className="space-y-2">
                    {LICENSE_INFO[selectedLicense].features.map((feature, i) => (
                      <li key={`feature-${feature}`} className="flex items-center gap-2 text-sm text-zinc-400">
                        <Check className="w-4 h-4 text-emerald-400" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
